import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ClienteContactosEditor } from "@/components/ClienteContactos";
import { useNavigate } from "react-router-dom";
import { ReactivacionDialog } from "@/components/ReactivacionDialog";
import { UserPlus, BarChart3, BellRing, Edit2, Trash2, CheckCircle2, XCircle, Clock, Repeat2, Link2, FileDown, Mail, Download, Upload, Search, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePagedList, PaginationBar } from "@/components/Pagination";

export default function Clients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reactivarOpen, setReactivarOpen] = useState(() => new URLSearchParams(window.location.search).get("reactivar") === "1");
  const puedeVerPerfil = (it) => user?.role !== "ventas" || user?.ver_todos_clientes || it.vendedor_id === user?.id;
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState("");
  // Filtrado por búsqueda (razón social, RUC, ciudad, nombre comercial, vendedor)
  const filteredItems = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const qn = norm(q);
    return items.filter(it => {
      const vendName = (vendedores.find(v => v.id === it.vendedor_id)?.nombres) || "";
      const hay = `${it.razon_social || ""} ${it.numero_identificacion || ""} ${it.nombre_comercial || ""} ${it.ciudad || ""} ${it.email || ""} ${vendName}`;
      return norm(hay).includes(qn);
    });
  }, [items, search, vendedores]);
  const pagedClientes = usePagedList(filteredItems, 20);
  function emptyForm() {
    return {
      tipo_identificacion: "ruc", numero_identificacion: "", tipo_persona: "juridica",
      razon_social: "", nombre_comercial: "", direccion_fiscal: "", ciudad: "", provincia: "",
      telefono: "", email: "", contacto: "", vendedor_id: "", tipo_cliente_id: "",
      correos: [],
      forma_pago_id: "", dias_credito: 0, cupo_credito: 0, limite_credito: "",
      cuenta_contable_id: "", contraparte_proveedor_id: "",
      direcciones_entrega: [],
      permite_fraccionamiento: false, permite_redondeo: false, observaciones: "",
    };
  }

  const load = async () => {
    try {
      const reqs = [
        api.get("/masters/clientes"),
        api.get("/masters/catalog/tipos_cliente"),
        api.get("/masters/catalog/formas_pago"),
        api.get("/masters/cuentas-auxiliares"),
        api.get("/users/basic"),  // disponible para todos los usuarios autenticados
      ];
      const [cli, tc, fp, cc, us] = await Promise.all(reqs);
      setItems(cli.data); setTipos(tc.data); setFormasPago(fp.data); setCuentas(cc.data || []);
      setVendedores((us?.data || []).filter(u => u.role === "ventas" || u.role === "admin" || u.role === "gerencia"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // ITER146 (Feb 2026): diálogo de recálculo de dias_credito en facturas pendientes
  const [recalcDialog, setRecalcDialog] = useState(null); // {cliente, dias, desde_numero}

  const save = async () => {
    try {
      const payload = {...form};
      ["vendedor_id", "tipo_cliente_id", "forma_pago_id", "cuenta_contable_id", "contraparte_proveedor_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
      if (payload.limite_credito === "" || payload.limite_credito === null) payload.limite_credito = null;
      else payload.limite_credito = parseFloat(payload.limite_credito);
      // ITER146: detectar cambio en dias_credito al editar para sugerir recálculo
      const prevDias = editing ? Number(editing.dias_credito || 0) : null;
      const newDias = Number(payload.dias_credito || 0);
      let savedCliente = null;
      if (editing) {
        const { data } = await api.patch(`/masters/clientes/${editing.id}`, payload);
        savedCliente = data || { ...editing, ...payload };
      } else {
        const { data } = await api.post("/masters/clientes", payload);
        savedCliente = data;
      }
      toast.success("Cliente guardado");
      setDialogOpen(false);
      load();
      // Si cambió dias_credito y ahora es > 0, abrir el diálogo de recálculo
      if (editing && prevDias !== newDias && newDias > 0) {
        setRecalcDialog({ cliente: savedCliente, dias: newDias, desde_numero: "", solo_pendientes: true });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ejecutarRecalculo = async () => {
    if (!recalcDialog?.cliente?.id) return;
    try {
      const params = new URLSearchParams();
      if (recalcDialog.desde_numero) params.set("desde_numero", recalcDialog.desde_numero);
      params.set("solo_pendientes", recalcDialog.solo_pendientes ? "true" : "false");
      const url = `/invoicing/facturas/recalcular-dias-credito-cliente/${recalcDialog.cliente.id}?${params.toString()}`;
      const { data } = await api.post(url);
      toast.success(`✓ ${data.facturas_actualizadas} de ${data.facturas_revisadas} facturas actualizadas con ${data.dias_aplicados} días`);
      setRecalcDialog(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar cliente ${it.razon_social}?`)) return;
    try { await api.delete(`/masters/clientes/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const setEstado = async (it, estado) => {
    try { await api.patch(`/masters/clientes/${it.id}/estado`, { estado }); toast.success("Estado actualizado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const replicarProveedor = async (it) => {
    if (it.contraparte_proveedor_id) {
      toast.info(`Ya está vinculado al proveedor ${it.contraparte_proveedor_id.slice(0,8)}…`);
      return;
    }
    if (!window.confirm(`¿Crear ficha de proveedor espejo para "${it.razon_social}"?\nLos archivos quedarán vinculados pero separados.`)) return;
    try {
      const { data } = await api.post(`/masters/clientes/${it.id}/replicar-proveedor`);
      toast.success(data.ya_existia ? "Ya existía, vinculado correctamente" : "Proveedor creado y vinculado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadEstadoCuenta = async (it) => {
    try {
      const r = await api.get(`/accounting/estado-cuenta-cliente/${it.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estado_cuenta_${(it.razon_social || "cliente").substring(0, 30).replace(/\s+/g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enviarEstadoCuentaEmail = async (it) => {
    if (!it.email) {
      toast.error("Este cliente no tiene email registrado. Edite el cliente para agregarlo.");
      return;
    }
    if (!window.confirm(`¿Enviar estado de cuenta a ${it.email}?`)) return;
    const mensaje = window.prompt("Mensaje personalizado (opcional, dejar en blanco para usar el default):") || "";
    try {
      const { data } = await api.post(`/accounting/estado-cuenta-cliente/${it.id}/enviar-email`, {
        mensaje_personalizado: mensaje,
      });
      toast.success(`Email enviado a ${data.destino}. Saldo: $${data.saldo.toFixed(2)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const estadoBadge = (e) => ({
    pendiente: <Badge className="fefo-critico border"><Clock size={10} className="mr-1" />Pendiente</Badge>,
    aprobado: <Badge className="fefo-safe border"><CheckCircle2 size={10} className="mr-1" />Aprobado</Badge>,
    rechazado: <Badge className="fefo-vencido border"><XCircle size={10} className="mr-1" />Rechazado</Badge>,
    bloqueado: <Badge variant="outline">Bloqueado</Badge>,
  }[e] || <Badge variant="outline">{e}</Badge>);

  const downloadReasignacionExcel = async () => {
    try {
      const r = await api.get("/masters/clientes/export-reasignacion-vendedores", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "reasignacion_vendedores.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado. Edita la columna 'Vendedor asignar' y súbelo.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadReasignacionExcel = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!window.confirm(`¿Aplicar reasignación masiva desde "${file.name}"? Esta acción reescribe el vendedor de los clientes que hayas cambiado.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/masters/clientes/import-reasignacion-vendedores", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let msg = `✓ ${data.cambios_aplicados} cambios aplicados`;
      if (data.sin_match_por_ruc > 0) msg += ` · ${data.sin_match_por_ruc} RUC sin match`;
      if ((data.valores_invalidos || []).length > 0) msg += ` · ${data.valores_invalidos.length} valores inválidos`;
      toast.success(msg, { duration: 8000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Clientes</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Los clientes pasan por flujo de aprobación antes de poder usarse en ventas.</p>
        </div>
        <div className="flex items-center gap-2">
          {(user?.role === "admin" || user?.role === "gerencia") && (
            <>
              <Button variant="outline" onClick={downloadReasignacionExcel} size="sm" data-testid="export-reasignacion-btn" title="Descargar Excel con dropdown de vendedores">
                <Download size={14} className="mr-1.5" /> Export reasignación
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".xlsx" onChange={uploadReasignacionExcel} className="hidden" data-testid="import-reasignacion-input" />
                <Button variant="outline" size="sm" asChild>
                  <span className="cursor-pointer inline-flex items-center"><Upload size={14} className="mr-1.5" /> Import reasignación</span>
                </Button>
              </label>
            </>
          )}
          <Button variant="outline" onClick={() => setReactivarOpen(true)} className="text-[#b53a25] border-[#f2c4bb]" data-testid="reactivacion-open-btn">
            <BellRing size={15} className="mr-2" /> Por reactivar
          </Button>
          {reactivarOpen && <ReactivacionDialog onClose={() => setReactivarOpen(false)} />}
          <Button onClick={() => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-client-btn">
            <UserPlus size={16} className="mr-2" /> Nuevo cliente
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-[#d1dbd6] bg-[#f7f9f8] flex items-center gap-2">
          <Search size={14} className="text-[#5c7368] flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por razón social, RUC, ciudad, email o vendedor…"
            className="flex-1 h-8 bg-transparent text-sm focus:outline-none placeholder:text-[#8ba397]"
            data-testid="clients-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-[#5c7368] hover:text-[#15241d]"
              data-testid="clients-search-clear"
              title="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
          <span className="text-[11px] text-[#5c7368] tabular-nums" data-testid="clients-search-count">
            {filteredItems.length} / {items.length}
          </span>
        </div>
        <table className="erp-table">
          <thead><tr><th>Identificación</th><th>Razón social</th><th>Ciudad</th><th>Estado</th><th>Cupo</th><th>Vendedor</th><th className="w-44"></th></tr></thead>
          <tbody>
            {pagedClientes.paged.map(it => (
              <tr key={it.id} data-testid={`client-row-${it.id}`}>
                <td className="font-mono text-xs">{it.numero_identificacion}</td>
                <td className="font-medium">{it.razon_social}</td>
                <td>{it.ciudad || "—"}</td>
                <td>{estadoBadge(it.estado)}</td>
                <td className="text-right tabular-nums">${(it.cupo_credito||0).toFixed(0)}</td>
                <td className="text-xs text-[#4a5c53]">{vendedores.find(v => v.id === it.vendedor_id)?.nombres || "—"}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {it.estado !== "aprobado" && <Button size="sm" variant="outline" className="text-[#205c45]" onClick={() => setEstado(it, "aprobado")} data-testid={`approve-${it.id}`}>Aprobar</Button>}
                    {puedeVerPerfil(it) && (
                      <Button size="sm" variant="outline" className="text-[#205c45]" onClick={() => navigate(`/clientes/${it.id}/perfil`)} title="Perfil y análisis del cliente" data-testid={`perfil-cliente-${it.id}`}>
                        <BarChart3 size={14} className="mr-1" />Perfil
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => downloadEstadoCuenta(it)} title="Descargar estado de cuenta PDF" data-testid={`estado-cuenta-${it.id}`}>
                      <FileDown size={16} className="text-[#2d4a73]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => enviarEstadoCuentaEmail(it)} title={it.email ? `Enviar estado a ${it.email}` : "Sin email"} disabled={!it.email} data-testid={`email-estado-${it.id}`}>
                      <Mail size={16} className={it.email ? "text-[#205c45]" : "text-[#aaa]"} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => replicarProveedor(it)} title={it.contraparte_proveedor_id ? "Ya vinculado a proveedor" : "Replicar como proveedor"} data-testid={`replicate-prov-${it.id}`}>
                      {it.contraparte_proveedor_id ? <Link2 size={16} className="text-[#205c45]" /> : <Repeat2 size={16} className="text-[#5c7368]" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(it); setForm({...emptyForm(), ...it}); setDialogOpen(true); }}><Edit2 size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredItems.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">{search ? `Sin coincidencias para "${search}"` : "Sin clientes registrados"}</td></tr>}
          </tbody>
        </table>
        <PaginationBar page={pagedClientes.page} total={pagedClientes.total} pageSize={pagedClientes.pageSize} onPrev={pagedClientes.prev} onNext={pagedClientes.next} label="clientes" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} cliente</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo identificación</Label>
              <Select value={form.tipo_identificacion} onValueChange={(v) => setForm({...form, tipo_identificacion: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ruc">RUC</SelectItem>
                  <SelectItem value="cedula">Cédula</SelectItem>
                  <SelectItem value="pasaporte">Pasaporte</SelectItem>
                  <SelectItem value="consumidor_final">Consumidor final</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Número</Label><Input value={form.numero_identificacion} onChange={(e) => setForm({...form, numero_identificacion: e.target.value})} data-testid="client-id-input" /></div>
            <div className="col-span-2"><Label>Razón social</Label><Input value={form.razon_social} onChange={(e) => setForm({...form, razon_social: e.target.value})} data-testid="client-razon-input" /></div>
            <div><Label>Nombre comercial</Label><Input value={form.nombre_comercial} onChange={(e) => setForm({...form, nombre_comercial: e.target.value})} /></div>
            <div>
              <Label>Tipo persona</Label>
              <Select value={form.tipo_persona} onValueChange={(v) => setForm({...form, tipo_persona: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="juridica">Jurídica</SelectItem>
                  <SelectItem value="natural">Natural</SelectItem>
                  <SelectItem value="extranjera">Extranjera</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Dirección fiscal</Label><Input value={form.direccion_fiscal} onChange={(e) => setForm({...form, direccion_fiscal: e.target.value})} /></div>
            <div><Label>Ciudad</Label><Input value={form.ciudad} onChange={(e) => setForm({...form, ciudad: e.target.value})} /></div>
            <div><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => setForm({...form, provincia: e.target.value})} /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} /></div>
            <div><Label>Contacto</Label><Input value={form.contacto} onChange={(e) => setForm({...form, contacto: e.target.value})} /></div>

            {/* ITER227: varios correos y varias direcciones de entrega */}
            <ClienteContactosEditor
              correos={form.correos || []}
              direcciones={form.direcciones_entrega || []}
              onChange={({ correos, direcciones_entrega }) => setForm({ ...form, correos, direcciones_entrega })}
            />
            {(user?.role === "admin" || user?.role === "gerencia" || user?.role === "contabilidad") && (
              <div>
                <Label>Vendedor asignado</Label>
                <Select value={form.vendedor_id || ""} onValueChange={(v) => setForm({...form, vendedor_id: v})}>
                  <SelectTrigger data-testid="client-vendedor-select"><SelectValue placeholder={vendedores.length ? "Seleccionar vendedor…" : "Sin usuarios disponibles"} /></SelectTrigger>
                  <SelectContent>
                    {vendedores.length === 0 && (
                      <div className="px-3 py-2 text-xs text-[#8ba397]">No hay vendedores activos. Active el usuario en Seguridad &gt; Usuarios.</div>
                    )}
                    {vendedores.map(v => <SelectItem key={v.id} value={v.id}>{v.nombres} {v.apellidos} <span className="text-[10px] text-[#5c7368]">({v.role})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Días crédito</Label><Input type="number" value={form.dias_credito} onChange={(e) => setForm({...form, dias_credito: parseInt(e.target.value)||0})} /></div>
            <div><Label>Cupo crédito (USD)</Label><Input type="number" step="0.01" value={form.cupo_credito} onChange={(e) => setForm({...form, cupo_credito: parseFloat(e.target.value)||0})} /></div>
            <div><Label>Límite crédito (USD)</Label><Input type="number" step="0.01" value={form.limite_credito} onChange={(e) => setForm({...form, limite_credito: e.target.value})} placeholder="Tope total adeudado" data-testid="client-limite-credito" /></div>
            <div>
              <Label>Cuenta contable (CxC)</Label>
              <Select value={form.cuenta_contable_id || ""} onValueChange={(v) => setForm({...form, cuenta_contable_id: v})}>
                <SelectTrigger data-testid="client-cuenta-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-72">{cuentas.filter(c => c.tipo === "activo").map(c => <SelectItem key={c.id} value={c.id}>{c.codigo} · {c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Forma de pago</Label>
              <Select value={form.forma_pago_id || ""} onValueChange={(v) => setForm({...form, forma_pago_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{formasPago.map(fp => <SelectItem key={fp.id} value={fp.id}>{fp.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo cliente</Label>
              <Select value={form.tipo_cliente_id || ""} onValueChange={(v) => setForm({...form, tipo_cliente_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-client-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ITER146 (Feb 2026): Diálogo de recálculo de días de crédito */}
      <Dialog open={!!recalcDialog} onOpenChange={(v) => { if (!v) setRecalcDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="recalc-dias-credito-dialog">
          <DialogHeader>
            <DialogTitle>Recalcular días de crédito en facturas</DialogTitle>
          </DialogHeader>
          {recalcDialog && (
            <div className="space-y-3">
              <p className="text-sm text-[#4a5c53]">
                Has actualizado los días de crédito de <b>{recalcDialog.cliente.razon_social}</b> a{" "}
                <b className="text-[#205c45]">{recalcDialog.dias} días</b>.
              </p>
              <p className="text-sm text-[#4a5c53]">
                ¿Quieres aplicar este cambio a sus facturas existentes? Se actualizarán los días de crédito y la fecha de vencimiento (y la CxC asociada). El XML enviado al SRI no se modifica.
              </p>
              <div className="space-y-2 mt-2">
                <Label className="text-xs">Aplicar desde N° de factura (opcional)</Label>
                <Input
                  placeholder="Ej: 5824, 001-002-000005824, o vacío para todas"
                  value={recalcDialog.desde_numero}
                  onChange={(e) => setRecalcDialog(r => ({ ...r, desde_numero: e.target.value }))}
                  data-testid="recalc-desde-numero"
                />
                <p className="text-[11px] text-[#5c7368]">
                  Deja vacío para aplicar a TODAS las facturas pendientes del cliente. Si lo llenas, sólo aplicará a facturas con número &gt;= ese valor.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={recalcDialog.solo_pendientes}
                  onChange={(e) => setRecalcDialog(r => ({ ...r, solo_pendientes: e.target.checked }))}
                  data-testid="recalc-solo-pendientes"
                />
                Solo facturas pendientes (recomendado)
              </label>
              <p className="text-[11px] text-[#5c7368]">
                Si lo desmarcas, también afectará facturas ya pagadas (cambia fecha de vencimiento retroactiva).
              </p>
              <p className="text-[11px] text-[#b08400]">
                ⚠ Las facturas de contado (forma de pago 01) NO se tocan.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecalcDialog(null)} data-testid="recalc-skip-btn">Omitir</Button>
            <Button onClick={ejecutarRecalculo} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="recalc-apply-btn">Recalcular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
